import React, { Fragment, useEffect } from 'react';
import { MenuItem, Select, FormControl, InputLabel } from '@mui/material';
import FilterAccordian from '../filterAccordian';
import Breadcrumb from '../common/breadcrumb';
import { userRequest } from '../../auth/requestMethods';
import Report from '@mui/icons-material/Report';
import CustomTable from '../tables/MuiDataGrid';
import { Eye } from 'react-feather';
import { isNaN } from 'lodash';

const Manage = ({ history, location }) => {
  useEffect(() => {
    document.title = 'PFSA | Reports | Delivery Run Summaries';
  }, []);
  const [perPage, setPerPage] = React.useState(10);
  const [page, setPage] = React.useState(1);
  const [dataIsFetching, setDataIsFetching] = React.useState(true);
  const [tableData, setTableData] = React.useState({
    count: 0,
    summaries: [],
  });

  const [filterOptions, setFilterOptions] = React.useState({
    deliveryRunTypes: [],
    schools: [],
    deliveryRuns: [],
  });
  const [searchInput, setSearchInput] = React.useState({
    school: 'All',
    id: 'All',
    delivery_run_type_id: 'All',
    planned_year: 'All',
  });

  const [path, setPath] = React.useState(new URLSearchParams(location.search).get('id'));

  React.useEffect(() => {
    if (path) {
      setSearchInput({ ...searchInput, id: path });
      setPath(null);
    }

    bulkAxios(['/schools/names', '/delivery-run-types/names', '/delivery-runs/quick-search'])
      .then((all) => {
        setFilterOptions({
          schools: all[0].schools,
          deliveryRunTypes: all[1].deliveryRunTypeNames,
          deliveryRuns: all[2].deliveryRuns,
        });
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  React.useEffect(() => {
    if (!path) {
      let queryString = buildQuery();

      userRequest()
        .get(`/delivery-runs/summaries?per_page=${perPage}&page=${page}&${queryString}`)
        .then(({ data }) => {
          console.log(data);
          setDataIsFetching(false);
          setTableData({
            count: data.total,
            summaries: data.summaries,
          });
        })
        .catch(({ response }) => {
          setDataIsFetching(false);
          console.log(response);
        });
    }
  }, [perPage, page, path, searchInput]);

  function getFilters() {
    return Object.entries(searchInput)
      .map(([key, value]) => {
        if (value !== '') {
          if (key === 'school') {
            if (value === 'All') {
              return [];
            } else {
              return `School: ${value}`;
            }
          } else if (key === 'id') {
            if (value === 'All') {
              return [];
            } else {
              return `Delivery Run: ${value}`;
            }
          } else if (key === 'delivery_run_type_id') {
            if (value === 'All') {
              return [];
            } else {
              return `Delivery Run Type:${filterOptions.deliveryRunTypes.find((x) => x.id === value)?.display_name}`;
            }
          } else if (key === 'planned_year') {
            if (value === 'All') {
              return [];
            } else {
              return `Planned Deliery Start:${value === '>' ? 'Future' : value}`;
            }
          }
        }
      })
      .filter((x) => x !== undefined && x.length !== 0);
  }

  function bulkAxios(urls = null) {
    if (!urls) return urls;

    let promises = urls.map((req) =>
      userRequest()
        .get(req)
        .then(({ data }) => {
          return data;
        })
        .catch((error) => {
          console.log(error);
          return null;
        })
    );

    return Promise.all(promises);
  }

  const actionMethods = {
    link: {
      icon: <Eye style={{ color: '#085993' }} />,
      toolTip: '/reports/details/...',
      getLink: (row) => `/reports/delivery-run-details/${row.id}`,
    },
  };

  const columns = [
    {
      field: 'school',
      headerName: 'School',
      flex: 1,
    },
    {
      field: 'sku_type',
      headerName: 'SKU Type',
      flex: 1,
    },
    {
      field: 'actual',
      headerName: 'Actual',
      flex: 1,
      valueGetter: ({ row }) => (row.actual ? formatNumber(row.actual ?? 0, 3) + ' ' + row.uom : '0'),
    },
    {
      field: 'target',
      headerName: 'Target',
      flex: 1,
      valueGetter: ({ row }) => (row.target ? formatNumber(row.target ?? 0, 3) + ' ' + (row.uom ?? '') : '0'),
    },
    {
      field: 'deviation',
      headerName: 'Deviation',
      flex: 1,
      valueGetter: ({ row }) => {
        const val = Math.round((100 - ((row.actual ?? 0) / (row.target ?? 0)) * 100) * 100) / 100;

        if (isNaN(val)) return '0 %';

        return val + ' %';
      },
    },
  ];

  const onChangeInput = ({ target: t }) => {
    setSearchInput({ ...searchInput, [t.name]: t.value });
  };

  function buildQuery() {
    let queryString = Object.entries(searchInput)
      .map(([key, value]) => {
        // Check if the value is not empty or "All" before adding to the query
        if (value !== '' && value !== 'All' && value !== 0) {
          return encodeURIComponent(key) + '=' + encodeURIComponent(value);
        }
        return '';
      })
      .filter((Q) => !!Q)
      .join('&');

    return queryString;
  }

  const onSearch = (e) => {
    e.preventDefault();

    const queryString = buildQuery();

    userRequest()
      .get(`/delivery-runs/summaries?per_page=${perPage}&page=${page}&${queryString}`)
      .then(({ data }) => {
        setTableData({
          count: data.total,
          summaries: data.summaries,
        });
      })
      .catch(({ response }) => {
        console.log(response);
      });
  };

  return (
    <Fragment>
      <Breadcrumb icon={<Report />} title="Summary" parent="Delivery Runs" />

      <div className="container-fluid">
        <FilterAccordian filters={getFilters()}>
          <form className="theme-form" noValidate="" onSubmit={onSearch}>
            <div className="form-group row mx-0">
              <FormControl>
                <InputLabel>Planned Delivery Start</InputLabel>
                <Select
                  name="planned_year"
                  value={searchInput.planned_year}
                  onChange={onChangeInput}
                  labelId="demo-simple-select-helper-label"
                  id="demo-simple-select-helper"
                  label="Planned Delivery Start"
                  style={{ fontStyle: searchInput.planned_year === 'All' ? 'italic' : 'normal' }}
                >
                  <MenuItem value={'All'} style={{ fontStyle: 'italic' }}>
                    All
                  </MenuItem>
                  <MenuItem value={'>'}>Future Only</MenuItem>
                  {[
                    ...new Set(filterOptions.deliveryRuns.map((val) => val.planned_delivery_start_date?.slice(0, 4))),
                  ].map((val, i) => (
                    <MenuItem key={i} value={val}>
                      {val}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div className="form-group row mx-0">
              <FormControl>
                <InputLabel>Delivery Run Type</InputLabel>
                <Select
                  name="delivery_run_type_id"
                  value={searchInput.delivery_run_type_id}
                  onChange={onChangeInput}
                  label="Delivery Run Type"
                  style={{ fontStyle: searchInput.delivery_run_type_id === 'All' ? 'italic' : 'normal' }}
                >
                  <MenuItem value={'All'} style={{ fontStyle: 'italic' }}>
                    All
                  </MenuItem>
                  {filterOptions.deliveryRunTypes.map((val) => (
                    <MenuItem key={val.id} value={val.id}>
                      {val.display_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div className="form-group row mx-0">
              <FormControl>
                <InputLabel id="demo-simple-select-helper-label">Delivery Run</InputLabel>
                <Select
                  name="id"
                  value={searchInput.id}
                  onChange={onChangeInput}
                  labelId="demo-simple-select-helper-label"
                  id="demo-simple-select-helper"
                  label="Delivery Run"
                  style={{ fontStyle: searchInput.id === 'All' ? 'italic' : 'normal' }}
                >
                  <MenuItem value={'All'} style={{ fontStyle: 'italic' }}>
                    All
                  </MenuItem>
                  {filterOptions.deliveryRuns.map((val, i) => (
                    <MenuItem key={i} value={val.id}>{`${val.display_name}: ${val.planned_delivery_start_date?.slice(
                      0,
                      10
                    )} (for ${val.consumption_start_date?.slice(0, 10)} to ${val.consumption_end_date?.slice(
                      0,
                      10
                    )})`}</MenuItem>
                  ))}
                  {}
                </Select>
              </FormControl>
            </div>
            <div className="form-group row mx-0">
              <FormControl>
                <InputLabel id="demo-simple-select-helper-label">School</InputLabel>
                <Select
                  name="school"
                  value={searchInput.school}
                  onChange={onChangeInput}
                  labelId="demo-simple-select-helper-label"
                  id="demo-simple-select-helper"
                  label="School"
                  style={{ fontStyle: searchInput.school === 'All' ? 'italic' : 'normal' }}
                >
                  <MenuItem value={'All'} style={{ fontStyle: 'italic' }}>
                    All
                  </MenuItem>
                  {filterOptions.schools.map((val) => (
                    <MenuItem key={val.id} value={val.display_name}>
                      {val.display_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </form>
        </FilterAccordian>
      </div>
      <br />
      <br />
      <div className="container-fluid">
        <CustomTable
          rows={tableData.summaries}
          actions={actionMethods}
          getRowId={(row) => row.id + row.sku_type + row.school}
          columns={columns}
          setPageSize={setPerPage}
          setPage={setPage}
          rowCount={tableData.count}
          pageSize={perPage}
          loading={dataIsFetching}
        />
      </div>
    </Fragment>
  );
};

function formatNumber(x) {
  var parts = x.toString().split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return parts.join('.');
}
export default Manage;
